import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FncoPreMarketingInfo from '@/components/Approval/@components/FncoPreMarketing';
import { APPROVAL_MARKETING_ANALYSIS_V2 } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { fetchApi } from '@/utils/api/customAxios';
import {
  getFncoAnalysisTitle,
  type FncoMarketingAnalysis,
} from '@/utils/approval/fncoPreMarketing';
import type { ApprovalFormValues } from '../types';

type FncoPreMarketingFieldProps = {
  /** 작성 진입 URL 의 analysisId. 편집 모드는 저장 attributes.analysisId */
  entryAnalysisId?: string;
  isEditMode: boolean;
};

/** F&CO 마케팅 사전 품의(221) 본문 위 폼 — 분석 레코드를 매 진입마다 다시 조회(레거시 getData) */
const FncoPreMarketingField = ({
  entryAnalysisId,
  isEditMode,
}: FncoPreMarketingFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { watch, setValue } = useFormContext<ApprovalFormValues>();
  const state = watch('fncoPreMarketing');
  const analysisId = entryAnalysisId ?? state?.analysisId;
  const loadedRef = useRef<string | null>(null);
  // 알림이 snackbar 를 새로 만들어 effect 가 다시 도는 무한 반복 차단
  const missingNoticeShownRef = useRef(false);

  const showLoadError = useCallback(
    () =>
      snackbar.showError({
        message: t('마케팅 분석 결과를 불러오지 못했습니다.'),
      }),
    [snackbar, t],
  );

  useEffect(() => {
    if (isEditMode || entryAnalysisId || missingNoticeShownRef.current) return;
    missingNoticeShownRef.current = true;
    showLoadError();
  }, [isEditMode, entryAnalysisId, showLoadError]);

  useEffect(() => {
    if (!analysisId || loadedRef.current === analysisId) return;
    loadedRef.current = analysisId;
    fetchApi<{ data?: FncoMarketingAnalysis }>({
      method: 'GET',
      url: `${APPROVAL_MARKETING_ANALYSIS_V2}/${encodeURIComponent(
        analysisId,
      )}`,
    })
      .then((res) => {
        const analysis = res?.data;
        if (!analysis || typeof analysis !== 'object') throw new Error('empty');
        setValue('fncoPreMarketing', { analysisId, analysis });
        // 레거시는 빈 title 로도 제목을 덮어 편집 시 저장 제목이 지워짐 — 값 있을 때만
        const title = getFncoAnalysisTitle(analysis);
        if (title) setValue('subject', title);
      })
      .catch(showLoadError);
  }, [analysisId, setValue, showLoadError]);

  return (
    <Box className="fnco-pre-marketing-form" sx={{ mb: '40px' }}>
      <FncoPreMarketingInfo analysis={state?.analysis ?? null} />
    </Box>
  );
};

export default FncoPreMarketingField;
